/**
 * cart-errors.js
 * ---------------------------------------------------------------------------
 * Single source of truth for talking to the Shopify Ajax Cart API and turning
 * every possible failure into a message a customer can actually act on.
 *
 * Why this exists
 * ---------------
 * Every add-to-cart path in this theme used to do:
 *
 *     const data = await response.json();
 *     if (!response.ok) { showToast(data.description || 'An unexpected error occurred.'); }
 *
 * That has two holes, and both of them produce the bare
 * "An unexpected error occurred." that customers have been reporting:
 *
 *   1. Shopify does NOT always answer with JSON. 429 (rate limited),
 *      430 (Shopify Security Rejection / bot challenge), 5xx and Cloudflare
 *      pages all return HTML. `response.json()` then throws, execution jumps
 *      straight to `.catch()`, and the generic message is shown even though
 *      the real problem is knowable (and usually retryable).
 *   2. A network drop / offline phone / timeout also lands in the same
 *      `.catch()` with no distinction.
 *
 * The magnet-bundle upload flow makes hole #1 much more likely: Upload Lift
 * fires one /cart/add.js per photo, so a 12- or 24-photo bundle bursts a dozen
 * or more cart writes in a couple of seconds, which is exactly what trips
 * Shopify's 429/430 throttling. That is why it reproduces on real customers
 * and not on a dev machine adding one item at a time.
 *
 * Documented / observed Ajax Cart API failures this module covers:
 *
 *   status 0    – network failure, offline, DNS, CORS, cancelled request
 *   AbortError  – our own timeout (request hung)
 *   400         – { status: "bad_request", message: "no valid id or line parameter" }
 *   401 / 403   – session expired, or storefront password protected
 *   404         – { status: 404, description: "Cannot find variant" }
 *   405 / 415   – wrong method / content type (theme bug, not customer's fault)
 *   422         – { status: 422, message: "Cart Error", description: "…" }
 *                 e.g. "You can't add more <item> to the cart."
 *                      "The product '<item>' is already sold out."
 *                 May also carry `errors: { quantity: [...] }` or
 *                 `errors: { "properties[Foo]": [...] }` for required
 *                 line-item properties (custom photo uploads live here).
 *   429         – rate limited, honours the Retry-After header
 *   430         – Shopify Security Rejection (bot / abuse challenge). HTML body,
 *                 identified by status or the X-Shopify-Security-Rejection header.
 *   5xx / 503   – Shopify or Cloudflare outage, storefront being deployed
 *   non-JSON 2xx– a proxy/app returned HTML where JSON was expected
 *
 * Usage
 * -----
 *     const result = await CartErrors.request('cart/add.js', { body: formData });
 *     if (!result.ok) { showToast(result.message); return; }
 *     // result.data is the parsed cart JSON
 *
 * Override any string from the theme by setting `window.CART_ERROR_STRINGS`
 * before this file runs.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var DEFAULT_STRINGS = {
    // Connectivity
    offline:
      "You appear to be offline. Check your connection and try adding to cart again.",
    network:
      "We couldn't reach the store. Check your connection and try again.",
    timeout:
      "That took too long to respond. Please try adding to cart again.",

    // Throttling / security — the bundle-upload culprits
    rate_limited:
      "You're going a little fast for our store. Please wait a few seconds and try again — your photos have not been lost.",
    rate_limited_wait:
      "You're going a little fast for our store. Please wait {seconds} seconds and try again — your photos have not been lost.",
    security_rejection:
      "Our store's security check blocked that request. Please wait a moment and try again, or reload the page if it keeps happening.",

    // Cart / product state
    sold_out:
      "Sorry, that item just sold out.",
    quantity_limit:
      "You've reached the maximum quantity available for that item.",
    variant_missing:
      "That option is no longer available. Please pick a different option and try again.",
    missing_photos:
      "Please upload your photos before adding this bundle to the cart.",
    cart_error:
      "We couldn't update your cart. Please review your selection and try again.",

    // Session / store
    session_expired:
      "Your session expired. Please reload the page and try again.",
    forbidden:
      "We couldn't complete that request. Please reload the page and try again.",
    not_found:
      "We couldn't find that item. Please reload the page and try again.",
    server_error:
      "Our store is having a moment. Please try again in a few seconds.",
    maintenance:
      "The store is briefly unavailable. Please try again in a minute.",

    // Fallbacks
    bad_response:
      "We got an unexpected response from the store. Please reload the page and try again.",
    theme_error:
      "Something went wrong on our side adding that to your cart. Please reload the page — if it keeps happening, contact us and we'll sort it out.",
    unknown:
      "We couldn't add that to your cart. Please try again — if it keeps happening, reload the page or contact us."
  };

  var STRINGS = DEFAULT_STRINGS;
  if (window.CART_ERROR_STRINGS) {
    STRINGS = {};
    Object.keys(DEFAULT_STRINGS).forEach(function (k) {
      STRINGS[k] = DEFAULT_STRINGS[k];
    });
    Object.keys(window.CART_ERROR_STRINGS).forEach(function (k) {
      if (window.CART_ERROR_STRINGS[k]) STRINGS[k] = window.CART_ERROR_STRINGS[k];
    });
  }

  var DEFAULT_TIMEOUT = 20000;
  var MAX_RETRIES = 2;

  function root() {
    return (
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      '/'
    );
  }

  function fill(template, values) {
    return template.replace(/\{(\w+)\}/g, function (_, key) {
      return values && values[key] != null ? values[key] : '';
    });
  }

  /** Pull the first usable sentence out of a 422 `errors` object. */
  function firstFieldError(errors) {
    if (!errors || typeof errors !== 'object') return null;
    var keys = Object.keys(errors);
    for (var i = 0; i < keys.length; i++) {
      var value = errors[keys[i]];
      var text = Array.isArray(value) ? value[0] : value;
      if (typeof text !== 'string' || !text.trim()) continue;
      // Shopify returns bare fragments like "is not present" for required
      // line-item properties — prefix them with the (human) field name.
      var field = keys[i].replace(/^properties\[(.*)\]$/, '$1');
      if (/^(is|can't|cannot|must|should|has)\b/i.test(text.trim())) {
        return field + ' ' + text.trim();
      }
      return text.trim();
    }
    return null;
  }

  function looksLikeMissingPhotos(data) {
    var blob = '';
    try {
      blob = JSON.stringify(data || {}).toLowerCase();
    } catch (e) {
      return false;
    }
    return (
      /properties\[/.test(blob) &&
      /(not present|blank|required|can't be blank)/.test(blob)
    );
  }

  /**
   * Map a completed (or failed) request to a customer-facing message.
   *
   * @param {Object} info
   * @param {number} info.status      HTTP status, 0 for network failures
   * @param {Object} [info.data]      parsed JSON body, if the body was JSON
   * @param {string} [info.bodyText]  raw body when it wasn't JSON
   * @param {Headers} [info.headers]
   * @param {Error}  [info.error]     thrown fetch/parse error
   * @returns {{message: string, code: string, retryable: boolean, retryAfter: number}}
   */
  function describe(info) {
    info = info || {};
    var status = info.status || 0;
    var data = info.data || null;
    var headers = info.headers || null;
    var error = info.error || null;

    var retryAfterHeader = headers && headers.get ? headers.get('Retry-After') : null;
    var retryAfter = parseInt(retryAfterHeader, 10);
    if (!(retryAfter > 0)) retryAfter = 0;

    // ---- No response at all -------------------------------------------------
    if (!status) {
      if (error && error.name === 'AbortError') {
        return result('timeout', STRINGS.timeout, true, 0);
      }
      if (
        typeof navigator !== 'undefined' &&
        navigator.onLine === false
      ) {
        return result('offline', STRINGS.offline, true, 0);
      }
      return result('network', STRINGS.network, true, 0);
    }

    // ---- Throttling and bot protection -------------------------------------
    if (status === 429) {
      var msg429 = retryAfter
        ? fill(STRINGS.rate_limited_wait, { seconds: retryAfter })
        : STRINGS.rate_limited;
      return result('rate_limited', msg429, true, retryAfter || 3);
    }

    var securityHeader =
      headers && headers.get ? headers.get('X-Shopify-Security-Rejection') : null;
    if (status === 430 || securityHeader) {
      return result('security_rejection', STRINGS.security_rejection, true, retryAfter || 3);
    }

    // ---- Server side --------------------------------------------------------
    if (status === 503) {
      return result('maintenance', STRINGS.maintenance, true, retryAfter || 5);
    }
    if (status >= 500) {
      return result('server_error', STRINGS.server_error, true, retryAfter || 2);
    }

    // ---- Client side --------------------------------------------------------
    if (status === 401) {
      return result('session_expired', STRINGS.session_expired, false, 0);
    }
    if (status === 403) {
      return result('forbidden', STRINGS.forbidden, false, 0);
    }
    if (status === 404) {
      var desc404 = data && data.description;
      if (desc404 && /variant/i.test(desc404)) {
        return result('variant_missing', STRINGS.variant_missing, false, 0);
      }
      return result('not_found', desc404 || STRINGS.not_found, false, 0);
    }
    if (status === 400 || status === 405 || status === 415) {
      // "no valid id or line parameter", wrong verb, wrong content type:
      // these are theme/integration bugs, never something the shopper did.
      if (window.console && console.error) {
        console.error('[cart] request rejected as malformed', status, data || info.bodyText);
      }
      return result('theme_error', STRINGS.theme_error, false, 0);
    }

    if (status === 422) {
      if (looksLikeMissingPhotos(data)) {
        return result('missing_photos', STRINGS.missing_photos, false, 0);
      }

      var description =
        (data && data.description) || firstFieldError(data && data.errors);

      if (description) {
        if (/sold out/i.test(description)) {
          return result('sold_out', description, false, 0);
        }
        if (/can'?t add more|maximum|only add/i.test(description)) {
          return result('quantity_limit', description, false, 0);
        }
        // Shopify's own wording is already shopper-friendly — show it verbatim.
        return result('cart_error', description, false, 0);
      }
      return result('cart_error', STRINGS.cart_error, false, 0);
    }

    // ---- 2xx that wasn't JSON, or anything unmapped -------------------------
    if (status >= 200 && status < 300) {
      return result('bad_response', STRINGS.bad_response, true, 0);
    }

    var fallback = (data && (data.description || data.message)) || STRINGS.unknown;
    return result('unknown', fallback, status >= 500, 0);

    function result(code, message, retryable, wait) {
      return {
        code: code,
        message: message,
        retryable: !!retryable,
        retryAfter: wait || 0,
        status: status,
        data: data
      };
    }
  }

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  /**
   * Read a response body once, preferring JSON but never throwing on HTML.
   */
  function readBody(response) {
    return response.text().then(function (text) {
      if (!text) return { data: null, bodyText: '' };
      try {
        return { data: JSON.parse(text), bodyText: text };
      } catch (e) {
        return { data: null, bodyText: text };
      }
    });
  }

  /**
   * Perform a cart request with a timeout, honest error mapping and bounded
   * retries for the transient failures (429 / 430 / 5xx / network).
   *
   * @param {string} path    e.g. 'cart/add.js' (root-relative is fine too)
   * @param {Object} options fetch options, plus:
   *                         { retries, timeout, json (object body) }
   * @returns {Promise<{ok: boolean, data: ?Object, status: number,
   *                    message: ?string, code: ?string, retryable: boolean}>}
   */
  function request(path, options) {
    options = options || {};

    var url = /^https?:\/\//.test(path) || path.charAt(0) === '/'
      ? path
      : root() + path;

    var retriesLeft = options.retries != null ? options.retries : MAX_RETRIES;
    var timeout = options.timeout != null ? options.timeout : DEFAULT_TIMEOUT;

    var init = {
      method: options.method || 'POST',
      headers: options.headers,
      body: options.body
    };
    if (options.json) {
      init.body = JSON.stringify(options.json);
      init.headers = Object.assign(
        { 'Content-Type': 'application/json', Accept: 'application/json' },
        options.headers || {}
      );
    }

    function attempt(triesRemaining) {
      var controller =
        typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = null;
      if (controller) {
        init.signal = controller.signal;
        timer = setTimeout(function () {
          controller.abort();
        }, timeout);
      }

      return fetch(url, init)
        .then(function (response) {
          if (timer) clearTimeout(timer);
          return readBody(response).then(function (body) {
            if (response.ok && body.data) {
              return { ok: true, data: body.data, status: response.status };
            }
            var problem = describe({
              status: response.status,
              data: body.data,
              bodyText: body.bodyText,
              headers: response.headers
            });
            return maybeRetry(problem, triesRemaining);
          });
        })
        .catch(function (error) {
          if (timer) clearTimeout(timer);
          var problem = describe({ status: 0, error: error });
          return maybeRetry(problem, triesRemaining);
        });
    }

    function maybeRetry(problem, triesRemaining) {
      if (problem.retryable && triesRemaining > 0) {
        var attemptIndex = retriesLeft - triesRemaining; // 0, 1, …
        var waitMs = problem.retryAfter
          ? problem.retryAfter * 1000
          : 600 * Math.pow(2, attemptIndex);
        if (window.console && console.warn) {
          console.warn(
            '[cart] ' + problem.code + ' (' + problem.status + ') — retrying in ' +
              waitMs + 'ms'
          );
        }
        return sleep(waitMs).then(function () {
          return attempt(triesRemaining - 1);
        });
      }
      return {
        ok: false,
        data: problem.data,
        status: problem.status,
        message: problem.message,
        code: problem.code,
        retryable: problem.retryable
      };
    }

    return attempt(retriesLeft);
  }

  window.CartErrors = {
    strings: STRINGS,
    describe: describe,
    request: request,
    /** Convenience for legacy call sites that already have a Response. */
    messageForResponse: function (response, data, bodyText) {
      return describe({
        status: response ? response.status : 0,
        data: data,
        bodyText: bodyText,
        headers: response ? response.headers : null
      }).message;
    },
    messageForThrown: function (error) {
      return describe({ status: 0, error: error }).message;
    }
  };
})();
